/** Cliente HTTP fino para a API interna do BFF. Sempre envia cookies (sessão) e o header CSRF em mutações. */

function readCsrfCookie(): string | null {
  const match = document.cookie.match(/(?:^|; )datasci_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : null;
}

export interface ApiErrorShape {
  error: { code: string; message: string };
}

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

function readAuthToken(path: string): string | null {
  if (typeof window === "undefined") return null;
  const key = path.startsWith("/admin/")
    ? "datasci_admin_token"
    : path.startsWith("/tenant/")
      ? "datasci_tenant_token"
      : "datasci_jwt";
  return localStorage.getItem(key);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");

  const token = readAuthToken(path);
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  if (method !== "GET" && method !== "HEAD") {
    const csrf = readCsrfCookie();
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }

  const response = await fetch(`/api${path}`, { ...init, headers, credentials: "include" });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorShape | null;
    throw new ApiError(body?.error.code ?? "unknown_error", body?.error.message ?? "Erro inesperado.", response.status);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function uploadForm<T>(path: string, formData: FormData): Promise<T> {
  const headers = new Headers();
  const token = readAuthToken(path);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const csrf = readCsrfCookie();
  if (csrf) headers.set("X-CSRF-Token", csrf);

  const response = await fetch(`/api${path}`, {
    method: "POST",
    headers,
    body: formData,
    credentials: "include",
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorShape | null;
    throw new ApiError(body?.error.code ?? "unknown_error", body?.error.message ?? "Erro no upload do arquivo.", response.status);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const apiClient = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  postForm: <T>(path: string, formData: FormData) => uploadForm<T>(path, formData),
};

